import Link from 'next/link'
import { Row } from '@christiankaindl/lyts'
import type { BlockObjectResponse } from '@notionhq/client/build/src/api-endpoints'
import Heading from '@shared/components/Heading/Heading'
import Text from '@shared/components/Text/Text'
import { blockChildren, blockRichText } from '@shared/lib/notion/blockPayload'
import { notionImageUrl } from '@shared/lib/notion/notionImageUrl'
import NotionText from './NotionText'
import * as styles from './notionBlock.css'

/**
 * `article` is long-form prose (the blog): larger, lighter body text.
 * `compact` is UI-embedded prose (settings, changelog): inherits app typography.
 */
export type NotionVariant = 'article' | 'compact'

export interface NotionBlocksProps {
  blocks: BlockObjectResponse[]
  variant?: NotionVariant
  /**
   * Shifts Notion headings down so they nest under the surrounding page's
   * headings — e.g. `1` renders a Notion `heading_3` as an `h4`.
   */
  headingOffset?: number
  /**
   * Floor for heading levels. An offset alone still lets a Notion `heading_1`
   * outrank the surrounding page (offset 1 would make it an `h2`), so a host
   * that owns its own outline sets the highest level Notion may use.
   */
  minHeadingLevel?: number
}

type ListBlock = Extract<
  BlockObjectResponse,
  { type: 'bulleted_list_item' | 'numbered_list_item' }
>

function isListBlock(block: BlockObjectResponse): block is ListBlock {
  return (
    block.type === 'bulleted_list_item' || block.type === 'numbered_list_item'
  )
}

type Grouping =
  /** `id` is the first item's block id, carried so the list has a stable key. */
  | { list: true; id: string; ordered: boolean; items: ListBlock[] }
  | { list: false; block: BlockObjectResponse }

/**
 * Notion returns list items as flat siblings, so the `<ul>`/`<ol>` wrapper has
 * to be reconstructed — one per run of same-kind items, not one per item.
 */
function groupBlocks(blocks: BlockObjectResponse[]): Grouping[] {
  const groups: Grouping[] = []

  for (const block of blocks) {
    if (!isListBlock(block)) {
      groups.push({ list: false, block })
      continue
    }
    const ordered = block.type === 'numbered_list_item'
    const open = groups.at(-1)
    if (open?.list && open.ordered === ordered) {
      open.items.push(block)
    } else {
      groups.push({ list: true, id: block.id, ordered, items: [block] })
    }
  }

  return groups
}

export function NotionImage({
  blockId,
  alt,
  variant = 'article',
  className,
  width,
  height,
}: {
  /** Notion block id for inline images, page id for a cover or an icon. */
  blockId: string
  alt: string
  variant?: NotionVariant
  /** Replaces the variant's styling outright — for avatars and other one-offs. */
  className?: string
  width?: number
  height?: number
}) {
  // Mirrored to S3 by `mirrorFiles`, because Notion's own URLs expire hourly.
  // Notion reports no intrinsic dimensions, so callers size it when they can.
  return (
    // oxlint-disable-next-line no-img-element
    <img
      src={notionImageUrl(blockId)}
      alt={alt}
      width={width}
      height={height}
      className={className ?? styles.image[variant]}
      loading='lazy'
    />
  )
}

function BodyText({
  variant,
  className,
  as,
  children,
}: {
  variant: NotionVariant
  className?: string
  /** `span` for list items: a `<p>` inside `<li>` picks up block paragraph rules. */
  as?: 'p' | 'span'
  children: React.ReactNode
}) {
  return variant === 'article' ? (
    <Text as={as} size='1.1em' weight={300} className={className}>
      {children}
    </Text>
  ) : (
    <Text as={as} className={className}>
      {children}
    </Text>
  )
}

function headingLevel(type: string, offset: number, min: number) {
  const base = Number(type.slice(-1))
  return Math.min(6, Math.max(base + offset, min)) as 1 | 2 | 3 | 4 | 5 | 6
}

function NotionBlock({
  block,
  variant,
  headingOffset,
  minHeadingLevel,
}: {
  block: BlockObjectResponse
  variant: NotionVariant
  headingOffset: number
  minHeadingLevel: number
}) {
  const children = blockChildren(block)

  switch (block.type) {
    case 'paragraph':
      return (
        <BodyText variant={variant}>
          <NotionText content={block.paragraph.rich_text} />
        </BodyText>
      )
    case 'heading_1':
    case 'heading_2':
    case 'heading_3': {
      return (
        <Heading
          h={headingLevel(block.type, headingOffset, minHeadingLevel)}
          className={styles.heading}
        >
          <NotionText content={blockRichText(block)} />
        </Heading>
      )
    }
    case 'to_do':
      return (
        <label htmlFor={block.id}>
          <input
            type='checkbox'
            id={block.id}
            defaultChecked={block.to_do.checked}
            disabled
          />{' '}
          <NotionText content={block.to_do.rich_text} />
        </label>
      )
    case 'toggle':
      return (
        <details>
          <summary>
            <NotionText content={block.toggle.rich_text} />
          </summary>
          <NotionBlocks
            blocks={children}
            variant={variant}
            headingOffset={headingOffset}
            minHeadingLevel={minHeadingLevel}
          />
        </details>
      )
    case 'child_page':
      return <Text>{block.child_page.title}</Text>
    case 'image':
      return (
        <NotionImage
          blockId={block.id}
          alt={block.image.caption?.[0]?.plain_text ?? ''}
          variant={variant}
        />
      )
    case 'divider':
      return <hr />
    case 'quote':
      return (
        <blockquote className={styles.quote}>
          <NotionText content={block.quote.rich_text} />
          <NotionBlocks
            blocks={children}
            variant={variant}
            headingOffset={headingOffset}
            minHeadingLevel={minHeadingLevel}
          />
        </blockquote>
      )
    case 'callout':
      return (
        <Row yAlign='start' gap={0.75} className={styles.callout[variant]}>
          {block.callout.icon?.type === 'emoji' && (
            <span className={styles.calloutIcon}>
              {block.callout.icon.emoji}
            </span>
          )}
          <BodyText variant={variant} className={styles.calloutText}>
            <NotionText content={block.callout.rich_text} />
          </BodyText>
        </Row>
      )
    default:
      return <NotionMediaBlock block={block} />
  }
}

/** Attachments and embeds, split out to keep the prose switch readable. */
function NotionMediaBlock({ block }: { block: BlockObjectResponse }) {
  switch (block.type) {
    case 'code':
      return (
        <pre className={styles.codeBlock}>
          <code>
            <NotionText content={block.code.rich_text} />
          </code>
        </pre>
      )
    case 'file': {
      const file = block.file
      const src = file.type === 'external' ? file.external.url : file.file.url
      const name = src.split('/').at(-1)?.split('?')[0] ?? 'Download'
      const caption = file.caption?.[0]?.plain_text
      return (
        <figure>
          <div>
            📎 <Link href={src}>{name}</Link>
          </div>
          {caption && <figcaption>{caption}</figcaption>}
        </figure>
      )
    }
    case 'bookmark':
      return (
        <a href={block.bookmark.url} target='_blank' rel='noreferrer noopener'>
          {block.bookmark.url}
        </a>
      )
    case 'video': {
      const video = block.video
      const src =
        video.type === 'external' ? video.external.url : video.file.url
      return <video controls src={src} />
    }
    default:
      // Visible on purpose: a silently dropped block is a page that quietly
      // lost a paragraph, which is worse than an obvious placeholder.
      return (
        <Text className={styles.unsupported} size='small'>
          ❌ Unsupported block: {block.type}
        </Text>
      )
  }
}

export default function NotionBlocks({
  blocks,
  variant = 'article',
  headingOffset = 0,
  minHeadingLevel = 1,
}: NotionBlocksProps) {
  return groupBlocks(blocks).map((group) => {
    if (!group.list) {
      return (
        <NotionBlock
          key={group.block.id}
          block={group.block}
          variant={variant}
          headingOffset={headingOffset}
          minHeadingLevel={minHeadingLevel}
        />
      )
    }
    const List = group.ordered ? 'ol' : 'ul'
    return (
      <List key={group.id} className={styles.list[variant]}>
        {group.items.map((item) => (
          <li key={item.id} className={styles.listItem}>
            {/* Same body typography as paragraphs — otherwise article list
                copy renders at the app default while paragraphs don't. */}
            <BodyText variant={variant} as='span'>
              <NotionText content={blockRichText(item)} />
            </BodyText>
            <NotionBlocks
              blocks={blockChildren(item)}
              variant={variant}
              headingOffset={headingOffset}
              minHeadingLevel={minHeadingLevel}
            />
          </li>
        ))}
      </List>
    )
  })
}
